import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Tag, Typography, Tooltip, Space, Dropdown, Grid, message, Spin } from 'antd';
import {
    CommentOutlined,
    UserOutlined,
    RobotOutlined,
    ClockCircleOutlined,
    MoreOutlined,
    EditOutlined,
    DeleteOutlined,
    ArrowRightOutlined,
    InboxOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { getTickets, updateTicket } from '../TicketServices';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocket from '../../../Hooks/useSocket';
import useVisualHighlight from '../../../Hooks/useVisualHighlight';
import { Resource, Action } from '../../../Constants/socket';
import { resolveBackendMessage } from '../../../Utils/apiMessages';

const { Text } = Typography;

const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];

const STATUS_CONFIG = {
    open: { color: '#0958d9', bg: 'rgba(22, 119, 255, 0.04)', border: '#1677ff', dot: '#1677ff' },
    in_progress: { color: '#d46b08', bg: 'rgba(250, 140, 22, 0.04)', border: '#fa8c16', dot: '#fa8c16' },
    resolved: { color: '#389e0d', bg: 'rgba(82, 196, 26, 0.04)', border: '#52c41a', dot: '#52c41a' },
    closed: { color: '#595959', bg: 'rgba(140, 140, 140, 0.04)', border: '#8c8c8c', dot: '#8c8c8c' },
};

const STATUS_CONFIG_DARK = {
    open: { color: '#69b1ff', bg: 'rgba(22, 119, 255, 0.08)', border: '#4096ff', dot: '#4096ff' },
    in_progress: { color: '#ffd666', bg: 'rgba(250, 140, 22, 0.08)', border: '#ffa940', dot: '#ffa940' },
    resolved: { color: '#95de64', bg: 'rgba(82, 196, 26, 0.08)', border: '#73d13d', dot: '#73d13d' },
    closed: { color: '#8c8c8c', bg: 'rgba(140, 140, 140, 0.08)', border: '#595959', dot: '#595959' },
};

const PRIORITY_COLORS = {
    urgent: '#ff4d4f',
    high: '#fa8c16',
    medium: '#1677ff',
    low: '#bfbfbf',
};

const CATEGORY_TAG_COLORS = {
    escalation: 'volcano',
    reclamation: 'magenta',
    suggestion: 'cyan',
    order: 'geekblue',
    other: 'default',
};

const PRIORITY_TAG_COLORS = {
    low: 'default',
    medium: 'blue',
    high: 'orange',
    urgent: 'red',
};

/**
 * Returns a human-friendly relative time string.
 */
const timeAgo = (dateString) => {
    if (!dateString) return '';
    try {
        const now = new Date();
        const date = new Date(dateString);
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'now';
        if (diffMins < 60) return `${diffMins}m`;
        const diffHrs = Math.floor(diffMins / 60);
        if (diffHrs < 24) return `${diffHrs}h`;
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays < 30) return `${diffDays}d`;
        return `${Math.floor(diffDays / 30)}mo`;
    } catch {
        return '';
    }
};

/**
 * Returns a short display ID like "#a3b8" from a full hashed ID.
 */
const shortId = (id) => {
    if (!id) return '';
    return `#${String(id).slice(0, 5).toUpperCase()}`;
};

// --- TicketCard component ---
const TicketCard = ({ ticket, isDarkMode, onDragStart, onDragEnd, draggingId, onStatusChange, highlightClass }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const priorityColor = PRIORITY_COLORS[ticket.priority] || '#bfbfbf';
    const isDragging = draggingId === ticket.id;

    const cardThemeClass = isDarkMode ? 'card-dark' : 'card-light';

    const quickActions = useMemo(() => {
        const otherStatuses = STATUSES.filter(s => s !== ticket.status);
        return {
            items: [
                ...otherStatuses.map(s => ({
                    key: `move_${s}`,
                    label: t(`ticket_status_${s}`),
                    icon: <ArrowRightOutlined />,
                })),
                { type: 'divider' },
                {
                    key: 'edit',
                    label: t('edit_ticket'),
                    icon: <EditOutlined />,
                },
                {
                    key: 'delete',
                    label: t('delete'),
                    icon: <DeleteOutlined />,
                    danger: true,
                },
            ],
        };
    }, [ticket.status, t]);

    const handleMenuClick = useCallback(({ key, domEvent }) => {
        domEvent.stopPropagation();
        if (key.startsWith('move_')) {
            const newStatus = key.replace('move_', '');
            onStatusChange(ticket.id, { status: newStatus });
        } else if (key === 'edit') {
            navigate(`/tickets/${ticket.id}`);
        }
    }, [ticket.id, onStatusChange, navigate]);

    return (
        <div
            className={`kanban-card ${cardThemeClass}${isDragging ? ' dragging' : ''} ${highlightClass || ''}`}
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', ticket.id);
                e.dataTransfer.effectAllowed = 'move';
                onDragStart(ticket.id);
            }}
            onDragEnd={onDragEnd}
            onClick={() => navigate(`/tickets/${ticket.id}`)}
            style={{
                background: isDarkMode ? '#141414' : '#ffffff',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.88)',
            }}
        >
            {/* Priority indicator bar */}
            <div className="card-priority-bar" style={{ background: priorityColor }} />

            {/* Header: ticket ID + actions */}
            <div className="card-header-row">
                <span className="card-ticket-id">{shortId(ticket.id)}</span>
                <Dropdown
                    menu={{ ...quickActions, onClick: handleMenuClick }}
                    trigger={['click']}
                    placement="bottomRight"
                >
                    <span
                        className="card-actions-btn"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            cursor: 'pointer',
                            padding: '2px 4px',
                            borderRadius: 4,
                            fontSize: 14,
                            color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.35)',
                        }}
                    >
                        <MoreOutlined />
                    </span>
                </Dropdown>
            </div>

            {/* Subject */}
            <div className="card-subject">
                {ticket.subject || t('no_subject')}
            </div>

            {/* Description preview */}
            {ticket.description && (
                <div className="card-description">
                    {ticket.description}
                </div>
            )}

            {/* Tags */}
            <div className="card-tags">
                {ticket.category && (
                    <Tag color={CATEGORY_TAG_COLORS[ticket.category] || 'default'}>
                        {t(`ticket_category_${ticket.category}`) || ticket.category}
                    </Tag>
                )}
                {ticket.priority && (
                    <Tag color={PRIORITY_TAG_COLORS[ticket.priority] || 'default'}>
                        {t(`ticket_priority_${ticket.priority}`) || ticket.priority}
                    </Tag>
                )}
            </div>

            {/* Footer */}
            <div className="card-footer">
                <Space size={8}>
                    {ticket.opened_by_type && (
                        <Tooltip title={t(`opened_by_${ticket.opened_by_type}`)}>
                            {ticket.opened_by_type === 'agent'
                                ? <RobotOutlined style={{ fontSize: 12 }} />
                                : <UserOutlined style={{ fontSize: 12 }} />
                            }
                        </Tooltip>
                    )}
                    {ticket.comment_count > 0 && (
                        <Space size={2}>
                            <CommentOutlined style={{ fontSize: 11 }} />
                            <span>{ticket.comment_count}</span>
                        </Space>
                    )}
                </Space>
                <Space size={2}>
                    <ClockCircleOutlined style={{ fontSize: 10 }} />
                    <span>{timeAgo(ticket.created_at)}</span>
                </Space>
            </div>
        </div>
    );
};

// --- KanbanColumn component ---
const KanbanColumn = ({ status, data, isDarkMode, onDrop, onDragStart, onDragEnd, draggingId, onStatusChange, onReachBottom, t, getHighlightClass }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const config = isDarkMode ? STATUS_CONFIG_DARK[status] : STATUS_CONFIG[status];
    const tickets = data?.items || [];

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        const ticketId = e.dataTransfer.getData('text/plain');
        if (ticketId) {
            onDrop(ticketId, status);
        }
    }, [onDrop, status]);

    return (
        <div
            className="kanban-column"
            style={{
                background: config.bg,
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`,
            }}
        >
            <div
                className="kanban-column-header"
                style={{
                    borderBottomColor: config.border,
                    color: config.color,
                }}
            >
                <span className="column-title">
                    <span className="column-dot" style={{ background: config.dot }} />
                    <span>{t(`ticket_status_${status}`) || status}</span>
                </span>
                <span
                    className="column-count"
                    style={{
                        background: config.color,
                        color: '#fff',
                    }}
                >
                    {tickets.length}
                </span>
            </div>

            <div
                className={`kanban-column-body${isDragOver ? ' drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onScroll={(e) => {
                    const { scrollTop, scrollHeight, clientHeight } = e.target;
                    if (scrollHeight - scrollTop <= clientHeight + 100) {
                        onReachBottom(status);
                    }
                }}
            >
                {tickets.length === 0 ? (
                    <div className={`kanban-empty-placeholder${isDarkMode ? ' dark' : ''}`}>
                        {data?.loading ? <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} /> : (
                            <>
                                <InboxOutlined style={{ fontSize: 22, marginBottom: 6, opacity: 0.4 }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {t('no_tickets_in_column')}
                                </Text>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        {tickets.map(ticket => (
                            <TicketCard
                                key={ticket.id}
                                ticket={ticket}
                                isDarkMode={isDarkMode}
                                onDragStart={onDragStart}
                                onDragEnd={onDragEnd}
                                draggingId={draggingId}
                                onStatusChange={onStatusChange}
                                highlightClass={getHighlightClass ? getHighlightClass(ticket.id, 'socket-highlight-target-card') : ''}
                            />
                        ))}
                        {data?.loading && (
                            <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// --- Main KanbanBoard component ---
const KanbanBoard = ({ search, filters, sortBy, sortDirection, refreshKey }) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const screens = Grid.useBreakpoint();
    const isCompact = !screens.md;
    const { handleError } = useErrorHandler();
    const { socket } = useSocket();
    
    // Visual highlights for real-time socket events
    const { highlightRow, getHighlightClass } = useVisualHighlight();

    const [draggingId, setDraggingId] = useState(null);
    const [activeTab, setActiveTab] = useState('open');
    const abortControllers = useRef({});

    const [columnData, setColumnData] = useState({
        open: { items: [], nextCursor: null, loading: false },
        in_progress: { items: [], nextCursor: null, loading: false },
        resolved: { items: [], nextCursor: null, loading: false },
        closed: { items: [], nextCursor: null, loading: false },
    });

    const fetchColumn = useCallback(async (status, overrideAfter = null, isRefresh = false) => {
        if (abortControllers.current[status] && isRefresh) {
            abortControllers.current[status].abort();
        }
        const controller = new AbortController();
        abortControllers.current[status] = controller;

        setColumnData(prev => ({
            ...prev,
            [status]: { ...prev[status], loading: true }
        }));

        try {
            const currentCursor = isRefresh ? null : overrideAfter;
            const res = await getTickets({
                size: 25,
                after: currentCursor,
                search,
                filters: { ...filters, status }, // specific column status
                sort_by: sortBy,
                sort_direction: sortDirection,
                signal: controller.signal
            });
            const newItems = res?.data || [];
            const nextCursor = res?.metadata?.next_cursor || null;

            setColumnData(prev => {
                const existingItems = isRefresh ? [] : prev[status].items;
                const existingMap = new Map(existingItems.map(i => [i.id, i]));
                newItems.forEach(i => existingMap.set(i.id, i));
                
                return {
                    ...prev,
                    [status]: {
                        items: Array.from(existingMap.values()),
                        nextCursor,
                        loading: false
                    }
                };
            });
        } catch (error) {
             if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
             handleError(error);
             setColumnData(prev => ({
                ...prev,
                [status]: { ...prev[status], loading: false }
             }));
        }
    }, [search, filters, sortBy, sortDirection, handleError]);

    useEffect(() => {
        STATUSES.forEach(status => fetchColumn(status, null, true));
    }, [fetchColumn, refreshKey]);

    const colDataRef = useRef(columnData);
    colDataRef.current = columnData;

    // Memoize all ticket IDs across columns for socket subscription
    const allTicketIds = useMemo(() => {
        return STATUSES.flatMap(status =>
            (columnData[status]?.items || []).map(ticket => ticket.id)
        ).filter(Boolean);
    }, [columnData]);

    // Subscribe to individual ticket IDs for UPDATE/DELETE events
    useSocketSubscription(Resource.TICKET, allTicketIds.length > 0 ? allTicketIds : null);

    const handleReachBottom = useCallback((status) => {
        const col = colDataRef.current[status];
        if (!col.loading && col.nextCursor) {
            fetchColumn(status, col.nextCursor, false);
        }
    }, [fetchColumn]);

    const handleStatusChange = async (ticketId, updates) => {
        if (!updates.status) return; // Only care about status
        const newStatus = updates.status;
        let oldStatus = null;
        let movingTicket = null;

        setColumnData(prev => {
            const nextState = { ...prev };
            STATUSES.forEach(s => {
                const idx = nextState[s].items.findIndex(t => t.id === ticketId);
                if (idx !== -1) {
                    oldStatus = s;
                    movingTicket = { ...nextState[s].items[idx], status: newStatus };
                    nextState[s].items = [...nextState[s].items];
                    nextState[s].items.splice(idx, 1);
                }
            });
            if (movingTicket && oldStatus !== newStatus) {
                nextState[newStatus].items = [movingTicket, ...nextState[newStatus].items];
            }
            return nextState;
        });

        try {
            const payload = await updateTicket(ticketId, updates);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
        } catch (error) {
            if (oldStatus) fetchColumn(oldStatus, null, true);
            fetchColumn(newStatus, null, true);
            handleError(error);
        }
    };

    const handleDrop = useCallback((ticketId, newStatus) => {
        let ticket = null;
        STATUSES.forEach(s => {
            const found = colDataRef.current[s].items.find(t => t.id === ticketId);
            if (found) ticket = found;
        });
        if (ticket && ticket.status !== newStatus) {
            handleStatusChange(ticketId, { status: newStatus });
        }
        setDraggingId(null);
    }, []);

    const handleDragStart = useCallback((id) => {
        setDraggingId(id);
    }, []);

    const handleDragEnd = useCallback(() => {
        setDraggingId(null);
    }, []);

    const handleTicketEvent = useCallback((data) => {
        STATUSES.forEach(status => fetchColumn(status, null, true));

        // Subscribe to new ticket from its creation time to cover the readiness gap
        if (data?.id && data?.created_at && socket) {
            socket.subscribeToResource(Resource.TICKET, [data.id], data.created_at);
        }
        if (data?.id) highlightRow(data.id);
    }, [fetchColumn, socket, highlightRow]);

    const handleTicketUpdated = useCallback((data) => {
        setColumnData(prev => {
            const nextState = { ...prev };
            let oldColumn = null;
            let ticketIndex = -1;

            // First, locate the ticket
            for (const s of STATUSES) {
                const idx = nextState[s].items.findIndex(t => t.id === data.id);
                if (idx !== -1) {
                    oldColumn = s;
                    ticketIndex = idx;
                    break;
                }
            }

            if (oldColumn) {
                const updatedTicket = { ...nextState[oldColumn].items[ticketIndex], ...data };
                const newColumn = updatedTicket.status || oldColumn;

                if (oldColumn === newColumn) {
                    // Status didn't change, just update inline
                    nextState[oldColumn] = {
                        ...nextState[oldColumn],
                        items: [...nextState[oldColumn].items]
                    };
                    nextState[oldColumn].items[ticketIndex] = updatedTicket;
                } else if (nextState[newColumn]) {
                    // Status changed, smoothly move the ticket out of the old column into the new one!
                    nextState[oldColumn] = {
                        ...nextState[oldColumn],
                        items: [...nextState[oldColumn].items]
                    };
                    nextState[oldColumn].items.splice(ticketIndex, 1);
                    nextState[oldColumn].total = Math.max(0, (nextState[oldColumn].total || 1) - 1);

                    nextState[newColumn] = {
                        ...nextState[newColumn],
                        items: [updatedTicket, ...nextState[newColumn].items],
                        total: (nextState[newColumn].total || 0) + 1
                    };
                }
            }
            return nextState;
        });
        if (data?.id) highlightRow(data.id, 'UPDATE');
    }, [highlightRow]);

    useSocketEvent(Resource.TICKET, Action.CREATE, handleTicketEvent);
    useSocketEvent(Resource.TICKET, Action.UPDATE, handleTicketUpdated);
    useSocketEvent(Resource.TICKET, Action.DELETE, handleTicketEvent);

    const isInitialSkeleton = Object.values(columnData).every(col => col.loading && col.items.length === 0);

    if (isInitialSkeleton) {
        return (
            <div className={isCompact ? 'kanban-board-mobile' : 'kanban-board'}>
                {isCompact ? (
                    <>
                        <div className="kanban-mobile-tabs">
                            {STATUSES.map(s => {
                                const cfg = isDarkMode ? STATUS_CONFIG_DARK[s] : STATUS_CONFIG[s];
                                return (
                                    <div key={s} className="kanban-mobile-tab" style={{ opacity: 0.5 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, display: 'inline-block' }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ padding: 8 }}>
                            {[1, 2, 3].map(i => (
                                <div key={i} className="kanban-skeleton-card" style={{ background: isDarkMode ? '#1f1f1f' : '#f5f5f5', height: 80, borderRadius: 10, marginBottom: 8 }} />
                            ))}
                        </div>
                    </>
                ) : (
                    STATUSES.map(status => {
                        const config = isDarkMode ? STATUS_CONFIG_DARK[status] : STATUS_CONFIG[status];
                        return (
                            <div key={status} className="kanban-column" style={{ background: config.bg, border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}` }}>
                                <div className="kanban-column-header" style={{ borderBottomColor: config.border, color: config.color }}>
                                    <span className="column-title">
                                        <span className="column-dot" style={{ background: config.dot }} />
                                        <span>{t(`ticket_status_${status}`)}</span>
                                    </span>
                                    <span className="column-count" style={{ background: config.color, color: '#fff' }}>—</span>
                                </div>
                                <div className="kanban-column-body">
                                    {[1, 2].map(i => (
                                        <div key={i} className="kanban-skeleton-card" style={{ background: isDarkMode ? '#1f1f1f' : '#f5f5f5', height: 90, borderRadius: 10 }} />
                                    ))}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        );
    }

    /* ─── Mobile: Tabbed Layout ─── */
    if (isCompact) {
        const activeConfig = isDarkMode ? STATUS_CONFIG_DARK[activeTab] : STATUS_CONFIG[activeTab];
        const activeCol = columnData[activeTab] || { items: [] };
        const activeTickets = activeCol.items;

        return (
            <div className="kanban-board-mobile">
                {/* Status Tab Bar */}
                <div className="kanban-mobile-tabs">
                    {STATUSES.map(status => {
                        const cfg = isDarkMode ? STATUS_CONFIG_DARK[status] : STATUS_CONFIG[status];
                        const count = columnData[status]?.items.length || 0;
                        const isActive = activeTab === status;
                        return (
                            <div
                                key={status}
                                className={`kanban-mobile-tab${isActive ? ' active' : ''}`}
                                onClick={() => setActiveTab(status)}
                                style={{
                                    borderBottomColor: isActive ? cfg.border : 'transparent',
                                    color: isActive ? cfg.color : (isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)'),
                                }}
                            >
                                <span style={{
                                    width: 7, height: 7, borderRadius: '50%',
                                    background: cfg.dot, display: 'inline-block',
                                    opacity: isActive ? 1 : 0.5,
                                }} />
                                <span className="kanban-mobile-tab-label">
                                    {t(`ticket_status_${status}`)}
                                </span>
                                <span
                                    className="kanban-mobile-tab-count"
                                    style={{
                                        background: isActive ? cfg.color : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'),
                                        color: isActive ? '#fff' : (isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'),
                                    }}
                                >
                                    {count}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Cards for active status */}
                <div
                    className="kanban-mobile-body"
                    style={{ background: activeConfig.bg, overflowY: 'auto' }}
                    onScroll={(e) => {
                        const { scrollTop, scrollHeight, clientHeight } = e.target;
                        if (scrollHeight - scrollTop <= clientHeight + 100) {
                            handleReachBottom(activeTab);
                        }
                    }}
                >
                    {activeTickets.length === 0 ? (
                        <div className={`kanban-empty-placeholder${isDarkMode ? ' dark' : ''}`}>
                            <InboxOutlined style={{ fontSize: 22, marginBottom: 6, opacity: 0.4 }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {t('no_tickets_in_column')}
                            </Text>
                        </div>
                    ) : (
                        <>
                            {activeTickets.map(ticket => (
                                <TicketCard
                                    key={ticket.id}
                                    ticket={ticket}
                                    isDarkMode={isDarkMode}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                    draggingId={draggingId}
                                    onStatusChange={handleStatusChange}
                                    highlightClass={getHighlightClass ? getHighlightClass(ticket.id, 'socket-highlight-target-card') : ''}
                                />
                            ))}
                            {activeCol.loading && (
                                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                    <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }

    /* ─── Desktop: Normal Columns ─── */
    return (
        <div className="kanban-board">
            {STATUSES.map(status => (
                <KanbanColumn
                    key={status}
                    status={status}
                    data={columnData[status]}
                    isDarkMode={isDarkMode}
                    onDrop={handleDrop}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    draggingId={draggingId}
                    onStatusChange={handleStatusChange}
                    onReachBottom={handleReachBottom}
                    t={t}
                    getHighlightClass={getHighlightClass}
                />
            ))}
        </div>
    );
};

export default KanbanBoard;
